/**
 * WidgetLivePreview — small inline preview strip beneath a widget's
 * editable header.  Pulls a KPI number or micro-bar series from
 * `/api/embed/v2/widget/preview` based on the widget's bound measure +
 * dimension.  Includes a source badge (real vs demo) so authors know
 * whether the number reflects physical data or the connector's
 * synthetic fallback.
 *
 * Split out of `EmbedContentViewerPage.js` in the iter-220 refactor —
 * component is self-contained (only React + fetch + API root) and has
 * no dependency on the parent's state, so moving it here is safe.
 */
import React from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export function WidgetLivePreview({ widget, token }) {
  const measureId = widget.config?.measure_id || '';
  const dimensionId = widget.config?.dimension_id || '';
  const filters = widget.config?.filters;
  const timeRange = widget.config?.time_range || '';
  const type = widget.widget_type || 'kpi';
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const filtersKey = React.useMemo(() => JSON.stringify(filters || []), [filters]);
  React.useEffect(() => {
    if (!token || !measureId) { setPreview(null); return; }
    let alive = true;
    setLoading(true);
    const url = new URL(`${API}/api/embed/v2/widget/preview`);
    url.searchParams.set('measure_id', measureId);
    if (dimensionId) url.searchParams.set('dimension_id', dimensionId);
    url.searchParams.set('widget_type', type);
    if (filtersKey && filtersKey !== '[]') url.searchParams.set('filters', filtersKey);
    if (timeRange) url.searchParams.set('time_range', timeRange);
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(body => { if (alive) setPreview(body); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [measureId, dimensionId, type, token, filtersKey, timeRange]);
  if (!measureId) {
    return (
      <div className="text-[9px] opacity-60 font-mono">bind a measure to preview</div>
    );
  }
  if (loading && !preview) {
    return <div className="text-[9px] opacity-60">loading…</div>;
  }
  if (!preview) return null;
  const sourceBadge = preview.source === 'aggregation'
    ? <span className="text-[8px] uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-300 px-1 py-[1px]">real</span>
    : <span className="text-[8px] uppercase tracking-wider rounded bg-amber-500/20 text-amber-300 px-1 py-[1px]" title="No physical data yet — showing synthetic preview">demo</span>;
  if (preview.kpi) {
    const trendCls = preview.kpi.trend === 'up' ? 'text-emerald-300' : 'text-rose-300';
    const cmp = preview.kpi.compare;
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`embed-viewer-preview-kpi-${widget.widget_id}`}>
        <span className="text-sm font-semibold">{preview.kpi.formatted}</span>
        <span className={`text-[9px] ${trendCls}`}>
          {preview.kpi.delta_pct >= 0 ? '+' : ''}{preview.kpi.delta_pct}%
        </span>
        {cmp && (
          <span
            className={`text-[9px] rounded px-1 py-[1px] ${cmp.trend === 'up' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}
            title={`Prior period: ${cmp.prior_value}`}
            data-testid={`embed-viewer-preview-compare-${widget.widget_id}`}
          >
            vs prior {cmp.delta_pct >= 0 ? '+' : ''}{cmp.delta_pct}%
          </span>
        )}
        {sourceBadge}
      </div>
    );
  }
  if (Array.isArray(preview.series) && preview.series.length > 0) {
    const maxV = Math.max(...preview.series.map(p => p.value)) || 1;
    return (
      <div data-testid={`embed-viewer-preview-series-${widget.widget_id}`}>
        <div className="flex items-end gap-[2px] h-6">
          {preview.series.map((p, i) => (
            <div
              key={i}
              title={`${p.label}: ${p.value}`}
              className="w-1.5 bg-current opacity-70 rounded-sm"
              style={{ height: `${Math.max(6, (p.value / maxV) * 100)}%` }}
            />
          ))}
        </div>
        <div className="mt-1">{sourceBadge}</div>
      </div>
    );
  }
  return null;
}

export default WidgetLivePreview;
